/**
 * @file Barre de navigation inferieure mobile pour le dashboard utilisateur.
 * @module layouts.app.bottom-nav
 *
 * Barre fixe en bas de l'ecran visible uniquement sur mobile (< lg).
 * Les tabs sont generes dynamiquement en fonction des modules et permissions
 * de l'utilisateur (bons cadeaux, wellness, restaurant).
 *
 * Architecture anti-layout-shift :
 * - styled components (pas de sx dynamique = pas de recalcul MUI a chaque render)
 * - React.memo sur le composant racine et sur chaque tab
 * - Une seule icone par tab (pas de swap Iconify entre active/inactive)
 * - Conteneur avec hauteur fixe et layer GPU dedie
 */

import React, {memo, useCallback, useMemo} from 'react';
// @mui
import {styled} from '@mui/material/styles';
import ButtonBase from '@mui/material/ButtonBase';
// hooks
import {usePermissions} from 'packages/common/hooks/session';
import {usePathname, useRouter} from 'packages/common/routes/hooks';
// components
import Iconify from 'packages/common/components/iconify';
// routes
import {paths} from 'packages/common/routes/paths';
// config
import {SAFE_AREA_X} from '../config-layout';
import {SETTINGS_FEATURES} from 'apps/dashboard/pages/app/settings/config/settingsSections';

const BOTTOM_NAV_HEIGHT = 56;

// ---------- Styled components (CSS statique, jamais recalcule) ----------

const NavRoot = styled('nav')(({theme}) => ({
  display: 'block',
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  height: `calc(${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px))`,
  // Le fond reste plein bord, les onglets se retirent de l'encoche en paysage
  paddingLeft: SAFE_AREA_X.LEFT,
  paddingRight: SAFE_AREA_X.RIGHT,
  boxSizing: 'border-box',
  zIndex: theme.zIndex.appBar + 1,
  backgroundColor: 'rgba(255, 255, 255, 0.92)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  borderTop: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.up('lg')]: {
    display: 'none',
  },
}));

const NavInner = styled('div')({
  display: 'flex',
  alignItems: 'stretch',
  height: BOTTOM_NAV_HEIGHT,
});

const TabButton = styled(ButtonBase)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
  padding: '6px 4px',
  minWidth: 0,
  position: 'relative',
  transition: 'color 0.2s ease',
});

const ActiveIndicator = styled('span')(({theme}) => ({
  position: 'absolute',
  top: 0,
  left: '25%',
  right: '25%',
  height: 3,
  borderRadius: '0 0 3px 3px',
  backgroundColor: theme.palette.navy[900],
}));

const TabLabel = styled('span')({
  fontSize: '0.625rem',
  lineHeight: 1.2,
  letterSpacing: 0.2,
});

// ---------- Tab individuel memo ----------

const NavTab = memo(function NavTab({tab, isActive, onNavigate}) {
  const handleClick = useCallback(() => {
    onNavigate(tab.path);
  }, [tab.path, onNavigate]);

  return (
    <TabButton
      onClick={handleClick}
      sx={{color: isActive ? 'navy.900' : 'text.secondary'}}
    >
      {isActive && <ActiveIndicator />}
      <Iconify icon={isActive ? tab.activeIcon : tab.icon} width={24} sx={{width: 24, height: 24, flexShrink: 0}} />
      <TabLabel style={{fontWeight: isActive ? 700 : 500}}>
        {tab.label}
      </TabLabel>
    </TabButton>
  );
});

// ---------- Composant principal ----------

/**
 * Barre de navigation inferieure mobile du dashboard utilisateur.
 *
 * Les tabs sont construits dynamiquement selon les modules et permissions :
 * - Dashboard (toujours visible)
 * - Bons cadeaux (si acces a une feature gift voucher)
 * - Wellness (si abonnement au module wellness)
 * - Restaurant (si abonnement au module restaurant)
 * - Parametres (si acces a une feature settings)
 *
 * @return {JSX.Element}
 */
const BottomNav = memo(function BottomNav() {
  const pathname = usePathname();
  const router = useRouter();
  const {hasModuleAccess, hasFeatureAccess, hasAnyFeatureAccess} = usePermissions();

  const visibleTabs = useMemo(() => {
    const tabs = [
      {
        key: 'home',
        label: 'Dashboard',
        path: paths.app.dashboard,
        icon: 'solar:widget-5-linear',
        activeIcon: 'solar:widget-5-bold',
      },
    ];

    // Bons cadeaux — premiere feature accessible comme point d'entree
    // Requiert l'activation du module bons cadeaux en plus des permissions feature
    if (hasModuleAccess('giftvouchers') && hasFeatureAccess('giftVoucher.manage')) {
      tabs.push({
        key: 'gift-vouchers',
        label: 'Bons cadeaux',
        path: paths.app.giftVoucher.manage,
        matchPrefix: paths.app.giftVoucher.root,
        icon: 'solar:gift-outline',
        activeIcon: 'solar:gift-bold',
      });
    } else if (hasModuleAccess('giftvouchers') && hasFeatureAccess('giftVoucher.catalog')) {
      tabs.push({
        key: 'gift-vouchers',
        label: 'Bons cadeaux',
        path: paths.app.giftVoucher.catalog.list,
        matchPrefix: paths.app.giftVoucher.root,
        icon: 'solar:gift-outline',
        activeIcon: 'solar:gift-bold',
      });
    }

    // Wellness — calendrier comme point d'entree principal
    if (hasModuleAccess('wellness') && hasFeatureAccess('wellness.calendar')) {
      tabs.push({
        key: 'wellness',
        label: 'Wellness',
        path: paths.app.wellness.calendar.root,
        matchPrefix: paths.app.wellness.root,
        icon: 'material-symbols:spa-outline',
        activeIcon: 'material-symbols:spa',
      });
    }

    // Restaurant — reservations comme point d'entree principal
    if (hasModuleAccess('restaurant') && hasFeatureAccess('restaurant.reservations')) {
      tabs.push({
        key: 'restaurant',
        label: 'Restaurant',
        path: paths.app.restaurant.reservations,
        matchPrefix: paths.app.restaurant.root,
        icon: 'mdi:silverware-fork-knife',
        activeIcon: 'mdi:silverware-fork-knife',
      });
    }

    // Parametres
    if (hasAnyFeatureAccess(SETTINGS_FEATURES)) {
      tabs.push({
        key: 'settings',
        label: 'Paramètres',
        path: paths.app.settings.root,
        icon: 'solar:settings-linear',
        activeIcon: 'solar:settings-bold',
      });
    }

    return tabs;
  }, [hasModuleAccess, hasFeatureAccess, hasAnyFeatureAccess]);

  const handleNavigate = useCallback((path) => {
    router.push(path);
  }, [router]);

  return (
    <NavRoot>
      <NavInner>
        {visibleTabs.map((tab) => {
          const isActive = pathname === tab.path ||
            (tab.matchPrefix && pathname.startsWith(tab.matchPrefix));

          return (
            <NavTab
              key={tab.key}
              tab={tab}
              isActive={isActive}
              onNavigate={handleNavigate}
            />
          );
        })}
      </NavInner>
    </NavRoot>
  );
});

export default BottomNav;
export {BOTTOM_NAV_HEIGHT};
